import React, { ChangeEvent, memo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from '@mui/material';

import { Typography } from '~/UIKit';
import { BORDER_COLOR, LIGHT_BLUE, NOTE_RED } from '~/Constants/Colors.constants';
import { useHasAccess, useLanguage } from '~/Hooks';
import { useLocations } from '~/store/provider';
import { requestDismissWarning, selectIsWarningDismissed } from '~/store/user';
import { makeStyles } from '~/UIKit/Style';
import { AccessList } from '~/Hooks/useHasAccess/accessList.types';
import { MapIcon } from '~/Components/Icons';
import { ModalSelectRadioGroupList } from '~/UIKit/Form/ModalSelect/listTypes';
import { ILocation } from '~/Models/DTO/Location.models';
import { IModalProps } from './types';
import { ModalSelectPopover } from './ModalSelectPopover';

const useStyles = makeStyles(
  () => ({
    infoText: {
      margin: '5px 0',
      fontSize: 12,
      lineHeight: '16px',
    },
    note: {
      background: LIGHT_BLUE,
      borderTop: `1px solid ${BORDER_COLOR}`,
      borderBottom: `1px solid ${BORDER_COLOR}`,
      margin: '0 -15px',
      padding: 15,
    },
    noteHighlighted: {
      color: NOTE_RED,
    },
  }),
  { name: 'ModalSelectLocationPopover' },
);

export const ModalSelectLocationPopover = memo(
  ({ open, name, onClose, anchorEl, selected, setFieldValue }: IModalProps<string>) => {
    const { classes } = useStyles();
    const { t } = useLanguage();
    const [searchQuery, setSearchQuery] = useState('');
    const dispatch = useDispatch();
    const { hasAccess } = useHasAccess();
    const { locationList } = useLocations();

    const dismissUSCWarning = () =>
      bindActionCreators(requestDismissWarning, dispatch)('uscLocationChangeWarning');

    const showUSCWarning =
      !useSelector(selectIsWarningDismissed('uscLocationChangeWarning')) &&
      !hasAccess(AccessList.HIDE_USC_LOCATION_WARNING_PARTNER_TOOL);

    const filteredLocations = locationList
      .filter(
        (location) =>
          location.id === selected ||
          location.name.toLowerCase().includes(searchQuery.toLowerCase()),
      )
      .map((location) => ({ ...location, icon: <MapIcon /> }));

    const handleSelect = (location: ILocation) => {
      setFieldValue(name, location.id);
      onClose();
    };

    const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(event.currentTarget.value);
    };

    return (
      <ModalSelectPopover
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        title={t('app.event.changeLocation')}
        actionName={t('app.checkin.select.done')}
        onActionTaken={onClose}
        search={{
          id: 'searchLocations',
          value: searchQuery,
          placeholder: t('app.event.changeLocation.searchForNewLocation'),
          handleSearch,
        }}
        info={
          showUSCWarning && (
            <div className={classes.note}>
              <Typography className={classes.infoText}>
                <strong className={classes.noteHighlighted}>{t('app.note')}&nbsp;</strong>
                {t('app.event.changeLocation.uscWarning')}
              </Typography>
              <Typography className={classes.infoText}>
                <Link onClick={dismissUSCWarning} underline="hover">
                  {t('app.doNotShowThisAgain')}
                </Link>
              </Typography>
            </div>
          )
        }
      >
        <ModalSelectRadioGroupList
          list={filteredLocations}
          onSelect={handleSelect}
          selectedId={selected}
        />
      </ModalSelectPopover>
    );
  },
);
